import { CardApiTagTypes } from "@/modules/card/common/enum";
import { cardApi, useRenameCardMutation } from "@/modules/card/services";
import { renameCardValidationSchema } from "@/modules/card/validations";
import { Notification } from "@/shared/components";
import { useAppDispatch, useNotify } from "@/shared/hooks";
import { FormContainer } from "@ab.uitools/ui-library/components/FormContainer";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Modal } from "@ab.uitools/ui-library/components/Modal";
import type { TFormData } from "@ab.uitools/ui-library/types/globalTypes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  cardId?: number;
  name?: string;
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
}

export const CardRenameDialog = ({ cardId, name, isOpen, onClose, refetch }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showErrorAlertNotification } = useNotify();
  const [renameCard, { isLoading }] = useRenameCardMutation();
  const [cardName, setCardName] = useState<string>(name || "");

  const handleSubmit = (data: TFormData, isDirty: boolean) => {
    if (isDirty && cardId) {
      renameCard({ cardId, data: { name: data.name } })
        .unwrap()
        .then(() => {
          refetch();
          dispatch(cardApi.util.invalidateTags([CardApiTagTypes.CardList]));
          onClose();
        })
        .catch((err) => {
          showErrorAlertNotification(err);
        });
    }
  };

  const initialValues = { name };

  return (
    <Modal
      closeIcon
      closeOnOutsideClick
      titleProps={{
        title: t("cards.renameTitle"),
        size: "large"
      }}
      isOpen={isOpen}
      onClose={onClose}
      className="rename-card-modal"
      onSubmit={() => {}}
      buttonProps={{
        confirm: {
          formId: "rename-card-form",
          buttonText: t("general.save"),
          buttonActionType: "submit",
          className: "rename-card-modal--submit-button",
          disabled: isLoading || cardName === name,
          isLoading: isLoading,
        },
        cancel: { buttonText: t("general.cancel") },
      }}
    >
      <Notification className="mb-8"/>
      <FormContainer
        mode="onBlur"
        reValidateMode="onBlur"
        className="full-width"
        formId="rename-card-form"
        initialValues={initialValues}
        onSubmit={(data, formState) => handleSubmit(data, formState.isDirty)}
        validationScheme={renameCardValidationSchema({ t })}
      >
        <FormField
          name="name"
          className="mb-32"
          errorMessageIcon="infoFilled"
          As={(props) => (
            <Input
              {...props}
              required
              onChange={(e) => setCardName(e.target.value)}
              autoComplete="name"
              label={t("cards.renameLabel")}
            />
          )}/>
      </FormContainer>
    </Modal>
  );
};
